import type { CancelTokenSource } from 'axios'
import { v4 as generateUUID } from 'uuid'
import { useEffect, useReducer, useRef } from 'react'
import axios from 'axios'

import createLocalThumbnail from '../createLocalThumbnail'

const CONCURRENT_UPLOADS = 4

export interface TCustomFile {
  id: string
  position: number
  originalFile: File
  fileName: string
  mediaType: string
  percentage: number
  status: Status
  cancelToken: CancelTokenSource
  hash?: string
  absoluteDownloadUrl?: string
  localPreview?: string
  src?: string
  width?: number
  height?: number
  alt?: string
  link?: string
  opentab?: boolean
}

type TState = {
  files: {
    pending: TCustomFile[]
    uploading: TCustomFile[]
    completed: TCustomFile[]
  }
  isProcessing: boolean
  messages: { fileName: string; err: any }[]
}

const INITIAL_STATE: TState = {
  files: {
    pending: [],
    uploading: [],
    completed: [],
  },
  isProcessing: false,
  messages: [],
}

type TUploadHelper = [
  files: {
    pending: TCustomFile[]
    uploading: TCustomFile[]
    completed: TCustomFile[]
  },
  addFiles: (files: File[]) => void,
  isProcessing: boolean,
  messages: { fileName: string; err: any }[],
  deleteFile: (id: string) => void,
  editFile: (file: ImmutableMap) => void,
]

export enum Status {
  PENDING = 'PENDING',
  UPLOADING = 'UPLOADING',
  COMPLETED = 'COMPLETED',
}

enum ActionTypes {
  ADD_FILES = 'ADD_FILES',
  EDIT_FILE = 'EDIT_FILE',
  SET_PROCESSING = 'SET_PROCESSING',
  UPDATE_UPLOADING_FILE = 'UPDATE_UPLOADING_FILE',
  UPDATE_FILE_PERCENTAGE = 'UPDATE_FILE_PERCENTAGE',
  MOVE_TO_LOADING = 'MOVE_TO_LOADING',
  MOVE_TO_COMPLETED = 'MOVE_TO_COMPLETED',
  DELETE_FILE = 'DELETE_FILE',
  ADD_MESSAGE = 'ADD_MESSAGE',
}

const reducer = (state: TState, action: { type: ActionTypes; value: any }) => {
  switch (action.type) {
    case ActionTypes.ADD_MESSAGE: // action.value: {fileName, err}
      return { ...state, messages: [...state.messages, action.value] }
    case ActionTypes.EDIT_FILE: {
      const mapEditedFiles = (file: TCustomFile) => (file.id === action.value.get('id') ? action.value.toJS() : file)

      return {
        ...state,
        files: {
          pending: state.files.pending.map(mapEditedFiles),
          uploading: state.files.uploading.map(mapEditedFiles),
          completed: state.files.completed.map(mapEditedFiles),
        },
      }
    }
    case ActionTypes.DELETE_FILE: {
      const filterNotDeletedFiles = (file: TCustomFile) => file.id !== action.value

      return {
        ...state,
        files: {
          pending: state.files.pending.filter(filterNotDeletedFiles),
          uploading: state.files.uploading.filter(filterNotDeletedFiles),
          completed: state.files.completed.filter(filterNotDeletedFiles),
        },
      }
    }
    case ActionTypes.ADD_FILES:
      return { ...state, files: { ...state.files, pending: state.files.pending.concat(action.value) } }
    case ActionTypes.SET_PROCESSING:
      return { ...state, isProcessing: action.value }
    case ActionTypes.UPDATE_UPLOADING_FILE: {
      const newUploadingFiles = state.files.uploading.map((i) => (i.id === action.value.id ? action.value : i))

      return {
        ...state,
        files: { ...state.files, uploading: newUploadingFiles },
      }
    }
    case ActionTypes.UPDATE_FILE_PERCENTAGE: {
      const updatedFiles = state.files.uploading.map((file) => {
        if (file.id === action.value.id) file.percentage = action.value.percentage
        return file
      })

      return {
        ...state,
        files: { ...state.files, uploading: updatedFiles },
      }
    }
    case ActionTypes.MOVE_TO_LOADING: {
      const fileToMove = state.files.pending.find((i) => i.id === action.value)

      if (!fileToMove) return state

      fileToMove.status = Status.UPLOADING

      const pending = state.files.pending.filter((i) => i.id !== fileToMove.id)
      const uploading = state.files.uploading.concat([fileToMove])

      return { ...state, files: { ...state.files, pending, uploading } }
    }

    case ActionTypes.MOVE_TO_COMPLETED: {
      const fileToMove = state.files.uploading.find((i) => i.id === action.value)

      if (!fileToMove) return state

      fileToMove.status = Status.COMPLETED

      const uploading = state.files.uploading.filter((i) => i.id !== fileToMove.id)
      const completed = state.files.completed.concat([fileToMove])

      return { ...state, files: { ...state.files, uploading, completed } }
    }
  }
}

const useUploadHelper = (storeFile: (file: File, config?: any) => Promise<Storage.Image>): TUploadHelper => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE)
  const processingTimeoutRef = useRef<number>(undefined)

  useEffect(() => {
    if (state.files.pending.length && state.files.uploading.length < CONCURRENT_UPLOADS) {
      uploadSingleFile(state.files.pending[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  const addFiles = async (files: File[]) => {
    // Show processing if longer than 500 ms
    processingTimeoutRef.current = window.setTimeout(() => {
      dispatch({ type: ActionTypes.SET_PROCESSING, value: true })
    }, 500)

    const customFiles: TCustomFile[] = []

    for (const file of files) {
      const result = await createCustomFile(file, files.indexOf(file))
      customFiles.push(result)
    }

    dispatch({ type: ActionTypes.ADD_FILES, value: customFiles })

    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current)
      processingTimeoutRef.current = 0
      dispatch({ type: ActionTypes.SET_PROCESSING, value: false })
    }
  }

  const createCustomFile = async (file: File, index: number): Promise<TCustomFile> => {
    const cancelTokenSource = axios.CancelToken.source()
    const customFile: TCustomFile = {
      id: generateUUID(), // temp id, no hash
      position: Date.now() + index,
      originalFile: file,
      localPreview: await createLocalThumbnail(file).catch(() => undefined),
      fileName: file.name,
      mediaType: file.type,
      percentage: 0,
      status: Status.PENDING,
      cancelToken: cancelTokenSource,
      alt: '',
      link: '',
      opentab: false,
    }

    return customFile
  }

  const uploadSingleFile = async (file: TCustomFile) => {
    const onUploadProgress = (event) => {
      const percentage = Math.round((100 * event.loaded) / event.total)

      // Update progress every 10%
      if (percentage - file.percentage > 10)
        dispatch({ type: ActionTypes.UPDATE_FILE_PERCENTAGE, value: { id: file.id, percentage } })
    }

    dispatch({ type: ActionTypes.MOVE_TO_LOADING, value: file.id })

    const config = { onUploadProgress, cancelToken: file.cancelToken.token }

    try {
      const res = await storeFile(file.originalFile, config)

      const uploadedFile: Storage.Image = res

      const newFile = Object.assign({}, file, uploadedFile)
      newFile.src = uploadedFile.absoluteDownloadUrl

      dispatch({ type: ActionTypes.UPDATE_UPLOADING_FILE, value: newFile })
      dispatch({ type: ActionTypes.MOVE_TO_COMPLETED, value: file.id })
    } catch (err) {
      dispatch({ type: ActionTypes.ADD_MESSAGE, value: { fileName: file.fileName, err } })
      dispatch({ type: ActionTypes.DELETE_FILE, value: file.id })
    }
  }

  const deleteFile = (id: string) => dispatch({ type: ActionTypes.DELETE_FILE, value: id })

  const editFile = (file: ImmutableMap) => dispatch({ type: ActionTypes.EDIT_FILE, value: file })

  return [state.files, addFiles, state.isProcessing, state.messages, deleteFile, editFile]
}

export default useUploadHelper
